import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { AsyncValue, RootState } from "../..";
import { GetAllUtilExecsQueryDto } from "../../../services/httpService";
import { getAssetsUtilizationExecs } from "../../../services/main/assetsService";
import { selectSelectedAssetsIds } from "../../user/assets-selection/assetsSelectionSlice";

export type UtilExec = Omit<GetAllUtilExecsQueryDto, "assetName" | "duration">;

export type UtilExecsState = AsyncValue<Array<UtilExec>>;

export const initialState: UtilExecsState = {
  value: [],
  pending: false,
};

export const loadUtilizationExecsAsync = createAsyncThunk(
  "utilExecs/loadUtilizationExecsAsync",
  async (_, { getState, dispatch }) => {
    const result = await getAssetsUtilizationExecs();
    return result;
  }
);

export const utilExecsSlice = createSlice({
  name: "taskExecs",
  initialState,
  reducers: {
    utilExecChanged: (state, action: PayloadAction<UtilExec>) => {
      state.value = state.value.map((ue) =>
        ue.assetId === action.payload.assetId ? action.payload : ue
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUtilizationExecsAsync.pending, (state, action) => {
        state.pending = true;
      })
      .addCase(loadUtilizationExecsAsync.fulfilled, (state, action) => {
        state.value = action.payload!;
        state.pending = false;
      })
      .addCase(loadUtilizationExecsAsync.rejected, (state, action) => {
        state.value = [];
        state.pending = false;
      });
  },
});

export const { utilExecChanged } = utilExecsSlice.actions;

export const selectUtilizationExecs = createSelector(
  (state: RootState) => state.main.utilExecs.value,
  (utilizationExecs) => utilizationExecs
);

export const selectUtilizationExecsByAssetId = createSelector(
  [selectUtilizationExecs, (state, assetId: string) => assetId],
  (utilizationExecs, assetId) =>
    utilizationExecs.find((ue) => ue.assetId === assetId)
);

export const selectSelectedAssetUtilizationExec = createSelector(
  selectSelectedAssetsIds,
  selectUtilizationExecs,
  (selectedAssets, utilizationExecs) =>
    utilizationExecs.filter((ue) =>
      selectedAssets.find((a) => a === ue.assetId)
    )
);

export const selectUtilizationExecsPending = createSelector(
  (state: RootState) => state.main.utilExecs.pending,
  (availableUtilRawsPending) => availableUtilRawsPending
);

export default utilExecsSlice.reducer;
